<template>
  <div class="twofa-overlay">
    <div class="twofa-card">
      <!-- Title -->
      <div class="twofa-header">
        <i class="material-icons twofa-lock-icon">lock</i>
        <h2 class="twofa-title">Verify your identity</h2>
      </div>

      <!-- Sending status stays above the form so the OTP fields stay usable -->
      <div v-if="sending" class="twofa-loading">
        <div
          class="mdl-spinner mdl-spinner--single-color mdl-js-spinner is-active"
        ></div>
        <p class="twofa-loading-text">Sending verification code…</p>
      </div>

      <template v-else>
        <p class="twofa-subtitle">
          A verification code was sent to
          <strong>{{ maskedEmail }}</strong>
        </p>

        <!-- TTL countdown -->
        <div class="twofa-ttl" :class="ttlClass">
          <i class="material-icons twofa-ttl-icon">access_time</i>
          <span v-if="codeExpired">Code expired — request a new one below</span>
          <span v-else-if="codeExpiry > 0">
            Expires in
            <strong>{{ expiryFormatted }}</strong>
          </span>
        </div>

        <div class="twofa-notice">
          <i class="material-icons twofa-notice-icon">info_outline</i>
          <span>
            This verification is valid for this session only. You will be asked
            to verify again if you close this tab or open the portal in a new
            window.
          </span>
        </div>
      </template>

      <!-- OTP fields stay enabled while the code is sending, verifying, or expired -->
      <div class="twofa-otp-row">
        <input
          v-for="(digit, i) in digits"
          :key="i"
          ref="otpDigits"
          type="tel"
          maxlength="1"
          inputmode="numeric"
          pattern="[0-9]*"
          autocomplete="one-time-code"
          :value="digit"
          :class="[
            'twofa-digit',
            {
              'twofa-digit--error': !!errorMessage,
              'twofa-digit--filled': !!digit
            }
          ]"
          @focus="$event.target.select()"
          @input="handleDigitInput(i, $event)"
          @keydown="handleDigitKeydown(i, $event)"
          @paste.prevent="handlePaste($event)"
        />
      </div>

      <!-- Error message -->
      <p v-if="errorMessage" class="twofa-error-msg">{{ errorMessage }}</p>

      <!-- Actions -->
      <div class="twofa-actions">
        <button
          class="mdl-button mdl-js-button mdl-button--raised mdl-button--colored twofa-btn-verify"
          :disabled="code.length < 6 || codeExpired || loading"
          @click="submit"
        >
          <span v-if="loading">Verifying…</span>
          <span v-else>Verify</span>
        </button>

        <div class="twofa-resend-row">
          <span class="twofa-resend-label">Didn't receive a code?</span>
          <button
            class="mdl-button mdl-js-button mdl-button--accent twofa-btn-resend"
            color="primary"
            :disabled="resendCooldown > 0 || loading || sending"
            @click="resend"
          >
            {{
              resendCooldown > 0
                ? `Resend in ${resendCooldown}s`
                : 'Resend code'
            }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {mapState, mapActions} from 'vuex';

export default {
  name: 'TwoFactorAuth',

  data() {
    return {
      sending: true,
      digits: ['', '', '', '', '', ''],
      loading: false,
      errorMessage: '',
      resendCooldown: 0,
      cooldownTimer: null,
      codeExpiry: 0,
      codeExpired: false,
      expiryTimer: null
    };
  },

  computed: {
    ...mapState(['twoFaMaskedEmail']),

    maskedEmail() {
      return this.twoFaMaskedEmail || '***@***';
    },

    code() {
      return this.digits.join('');
    },

    expiryFormatted() {
      const mins = Math.floor(this.codeExpiry / 60);
      const secs = this.codeExpiry % 60;
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    },

    ttlColor() {
      if (this.codeExpired || this.codeExpiry <= 30) return 'error';
      if (this.codeExpiry <= 60) return 'warning';
      return 'normal';
    },

    ttlClass() {
      if (this.codeExpired || this.codeExpiry <= 30) return 'twofa-ttl--danger';
      if (this.codeExpiry <= 60) return 'twofa-ttl--warning';
      return 'twofa-ttl--normal';
    }
  },

  watch: {
    // Auto-submit when all 6 digits are filled
    code(val) {
      if (val.length === 6 && !this.loading && !this.codeExpired) {
        this.$nextTick(() => this.submit());
      }
    }
  },

  async mounted() {
    this.focusFirstDigit();

    // Resume an unexpired code already emailed in this tab. A refresh must
    // not request another email while that code is still valid.
    const storedExpiry = sessionStorage.getItem('twofa_otp_expiry');
    if (storedExpiry) {
      const remaining = Math.floor((Number(storedExpiry) - Date.now()) / 1000);
      if (remaining > 0) {
        this.sending = false;
        this.startCodeExpiry(remaining);
        return;
      }
    }

    // Fresh session, or the previous code has already expired.
    try {
      const data = await this.request2fa();
      const ttl = (data && data.ttl) || 300;
      // A reused code keeps the original expiry. Do not start a resend cooldown.
      if (!data || !data.reused) {
        sessionStorage.setItem('twofa_otp_sent', '1');
        sessionStorage.setItem(
          'twofa_otp_expiry',
          String(Date.now() + ttl * 1000)
        );
        this.startCooldown();
      }
      this.startCodeExpiry(ttl);
    } catch (e) {
      this.errorMessage =
        'Failed to send verification code. Please use "Resend code" to try again.';
    } finally {
      this.sending = false;
      // MDL requires upgrading any new DOM elements it manages
      this.$nextTick(() => {
        if (window.componentHandler) componentHandler.upgradeAllRegistered();
      });
    }
  },

  beforeDestroy() {
    if (this.cooldownTimer) clearInterval(this.cooldownTimer);
    if (this.expiryTimer) clearInterval(this.expiryTimer);
  },

  methods: {
    ...mapActions('voter', ['verify2fa', 'request2fa']),

    focusFirstDigit() {
      this.$nextTick(() => {
        if (this.$refs.otpDigits && this.$refs.otpDigits[0]) {
          this.$refs.otpDigits[0].focus();
        }
      });
    },

    // ─── OTP digit input handlers ──────────────────────────────────────────

    handleDigitInput(i, event) {
      const val = event.target.value.replace(/\D/g, '').slice(-1);
      this.$set(this.digits, i, val);
      event.target.value = val;

      if (val && i < 5) {
        this.$nextTick(() => {
          this.$refs.otpDigits[i + 1].focus();
        });
      }
    },

    handleDigitKeydown(i, event) {
      if (event.key === 'Backspace') {
        event.preventDefault();
        if (this.digits[i]) {
          this.$set(this.digits, i, '');
        } else if (i > 0) {
          this.$set(this.digits, i - 1, '');
          this.$refs.otpDigits[i - 1].focus();
        }
      } else if (event.key === 'ArrowLeft' && i > 0) {
        this.$refs.otpDigits[i - 1].focus();
      } else if (event.key === 'ArrowRight' && i < 5) {
        this.$refs.otpDigits[i + 1].focus();
      }
    },

    handlePaste(event) {
      const pasted = (event.clipboardData || window.clipboardData)
        .getData('text/plain')
        .replace(/\D/g, '')
        .slice(0, 6);

      if (!pasted) return;

      pasted.split('').forEach((char, i) => {
        this.$set(this.digits, i, char);
      });

      const lastIdx = Math.min(pasted.length - 1, 5);
      this.$nextTick(() => {
        this.$refs.otpDigits[lastIdx].focus();
      });
    },

    // ─── Verify & Resend ───────────────────────────────────────────────────

    async submit() {
      if (this.code.length < 6 || this.loading || this.codeExpired) return;

      this.loading = true;
      this.errorMessage = '';

      try {
        await this.verify2fa({code: this.code});
        sessionStorage.removeItem('twofa_otp_sent');
        sessionStorage.removeItem('twofa_otp_expiry');
        sessionStorage.removeItem('twofa_otp_destination');
        this.$emit('verified');
      } catch (err) {
        const status = err && err.response && err.response.status;
        if (status === 401) {
          this.errorMessage = 'Invalid code. Please try again.';
        } else if (status === 400) {
          this.errorMessage = 'Please enter your verification code.';
        } else {
          this.errorMessage = 'Something went wrong. Please try again.';
        }
        this.digits = ['', '', '', '', '', ''];
        this.$nextTick(() => {
          if (this.$refs.otpDigits && this.$refs.otpDigits[0])
            this.$refs.otpDigits[0].focus();
        });
      } finally {
        this.loading = false;
      }
    },

    async resend() {
      this.loading = true;
      this.errorMessage = '';
      this.digits = ['', '', '', '', '', ''];

      try {
        const data = await this.request2fa({force: true});
        const ttl = (data && data.ttl) || 300;
        sessionStorage.setItem('twofa_otp_sent', '1');
        sessionStorage.setItem(
          'twofa_otp_expiry',
          String(Date.now() + ttl * 1000)
        );
        this.startCodeExpiry(ttl);
        this.startCooldown();
        this.codeExpired = false;
      } catch (e) {
        this.errorMessage = 'Could not resend code. Please try again.';
      } finally {
        this.loading = false;
        this.$nextTick(() => {
          if (this.$refs.otpDigits && this.$refs.otpDigits[0])
            this.$refs.otpDigits[0].focus();
        });
      }
    },

    // ─── Timers ───────────────────────────────────────────────────────────

    startCooldown(seconds = 30) {
      if (this.cooldownTimer) clearInterval(this.cooldownTimer);
      this.resendCooldown = seconds;
      this.cooldownTimer = setInterval(() => {
        this.resendCooldown--;
        if (this.resendCooldown <= 0) {
          clearInterval(this.cooldownTimer);
          this.cooldownTimer = null;
        }
      }, 1000);
    },

    startCodeExpiry(seconds) {
      if (this.expiryTimer) clearInterval(this.expiryTimer);
      this.codeExpiry = seconds;
      this.codeExpired = false;
      this.expiryTimer = setInterval(() => {
        this.codeExpiry--;
        if (this.codeExpiry <= 0) {
          clearInterval(this.expiryTimer);
          this.expiryTimer = null;
          this.codeExpired = true;
        }
      }, 1000);
    }
  }
};
</script>

<style scoped>
/* ── Centered within the normal content column ── */
.twofa-overlay {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 40px 16px;
}

/* ── Card ── */
.twofa-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.22);
  width: 100%;
  max-width: 420px;
  padding: 32px 28px 24px;
}

/* ── Header ── */
.twofa-header {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}

.twofa-lock-icon {
  font-size: 32px;
  color: #1565c0;
  margin-right: 8px;
}

.twofa-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: #212121;
  margin: 0;
}

/* ── Subtitle ── */
.twofa-subtitle {
  text-align: center;
  color: #555;
  font-size: 0.95rem;
  line-height: 1.5;
  margin: 0 0 8px;
}

/* ── Loading ── */
.twofa-loading {
  text-align: center;
  padding: 24px 0;
}

.twofa-loading-text {
  color: #757575;
  font-size: 0.9rem;
  margin-top: 12px;
}

/* ── TTL countdown ── */
.twofa-ttl {
  display: inline-flex;
  align-items: center;
  border-radius: 20px;
  padding: 3px 10px;
  font-size: 0.875rem;
  margin: 0 auto 16px;
  display: flex;
  justify-content: center;
}

.twofa-ttl-icon {
  font-size: 16px;
  margin-right: 4px;
}

.twofa-ttl--normal {
  background: #f5f5f5;
  color: #757575;
}

.twofa-ttl--warning {
  background: #fff8e1;
  color: #f57f17;
}

.twofa-ttl--danger {
  background: #fce4ec;
  color: #c62828;
}

/* ── Session notice ── */
.twofa-notice {
  display: flex;
  align-items: flex-start;
  background: #e3f2fd;
  border: 1px solid #90caf9;
  border-radius: 4px;
  padding: 10px 12px;
  font-size: 0.8rem;
  color: #1565c0;
  line-height: 1.4;
  margin-bottom: 20px;
}

.twofa-notice-icon {
  font-size: 18px;
  margin-right: 8px;
  flex-shrink: 0;
  margin-top: 1px;
}

/* ── OTP digit boxes ── */
.twofa-otp-row {
  display: flex;
  gap: 10px;
  justify-content: center;
  margin-bottom: 6px;
  position: relative;
  z-index: 1;
  pointer-events: auto;
}

.twofa-digit {
  width: 44px;
  height: 56px;
  text-align: center;
  font-size: 1.5rem;
  font-weight: 600;
  border: 2px solid #e0e0e0;
  border-radius: 6px;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
  color: #1565c0;
  background: #fff;
  cursor: text;
  opacity: 1;
  pointer-events: auto;
  caret-color: transparent;
  -webkit-appearance: none;
  appearance: none;
}

.twofa-digit:focus {
  border-color: #1565c0;
  box-shadow: 0 0 0 3px rgba(21, 101, 192, 0.18);
  background: #fff;
}

.twofa-digit.twofa-digit--filled {
  border-color: #1565c0;
  background: #fff;
}

.twofa-digit.twofa-digit--error {
  border-color: #f44336;
  box-shadow: none;
}

.twofa-digit::-webkit-inner-spin-button,
.twofa-digit::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* ── Error message ── */
.twofa-error-msg {
  color: #f44336;
  font-size: 0.8rem;
  text-align: center;
  margin: 6px 0 0;
}

/* ── Actions ── */
.twofa-actions {
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
}

.twofa-btn-verify {
  width: 100%;
  height: 40px;
  font-size: 0.95rem;
  margin-bottom: 12px;
}

.twofa-btn-verify:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.twofa-resend-row {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 4px;
}

.twofa-resend-label {
  font-size: 0.875rem;
  color: #757575;
}

.twofa-btn-resend {
  font-size: 0.875rem;
  min-width: 0;
  padding: 0 8px;
}

.twofa-btn-resend:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
</style>
